import { DateTime } from 'luxon'
import {
  formatCurrency,
  formatDecimal,
  formatPercent,
  getMonthOverMonth,
} from 'pages/pga-coach/coach-analytics/utils'

export const MONTH_VALUE_FORMAT = 'yyyy-MM'

export const getMonthValue = (now = DateTime.now()) =>
  now.toFormat(MONTH_VALUE_FORMAT)

// Covers the selected month and the one before it, as whole months.
export const getTimeRange = month => {
  const selected = DateTime.fromFormat(month, MONTH_VALUE_FORMAT)

  return {
    startDate: selected
      .minus({ months: 1 })
      .startOf('month')
      .toFormat('yyyy-MM-dd'),
    endDate: selected.endOf('month').toFormat('yyyy-MM-dd'),
  }
}

const valueFor = (byMonth, month, key) =>
  (byMonth || []).find(point => point.month === month)?.[key] || 0

// Private lessons plus group event enrollments, the same bookings the Coach
// Analytics Booking mix counts.
const sumBookings = (analytics, months) =>
  months.map(month => ({
    month,
    count:
      valueFor(analytics?.privateLessonTypes?.byMonth, month, 'count') +
      valueFor(analytics?.groupEvents?.byMonth, month, 'count'),
  }))

// `rebookingRateByMonth` returns `rate` as a 0-1 share (null when no student
// completed a lesson that month); the tile shows it as a percent.
const rebookingRateSeries = analytics =>
  analytics?.rebookingRateByMonth?.map(({ month, rate }) => ({
    month,
    rate: rate == null ? null : rate * 100,
  }))

// `optional` tiles show "--" and no change when the selected month has no
// value, rather than a zero.
const TILE_CONFIG = [
  {
    id: 'total-bookings',
    label: 'Total bookings',
    getSeries: (analytics, months) => sumBookings(analytics, months),
    dataKey: 'count',
    format: formatDecimal,
  },
  {
    id: 'booking-value',
    label: 'Booking value',
    getSeries: analytics => analytics?.earnings?.byMonth,
    dataKey: 'total',
    format: formatCurrency,
  },
  {
    id: 'rebooking-rate',
    label: 'Rebooking rate',
    getSeries: rebookingRateSeries,
    dataKey: 'rate',
    format: formatPercent,
    optional: true,
  },
  {
    id: 'new-students',
    label: 'New students',
    getSeries: analytics => analytics?.newStudentsByMonth,
    dataKey: 'count',
    format: formatDecimal,
  },
]

const PLACEHOLDER_VALUE = '--'

// `deltaPercent` is null without prior-month activity, so no comparison shows.
export const getTiles = (analytics, month) => {
  const selected = DateTime.fromFormat(month, MONTH_VALUE_FORMAT)
  const months = [
    getMonthValue(selected.minus({ months: 1 })),
    getMonthValue(selected),
  ]

  return TILE_CONFIG.map(
    ({ id, label, getSeries, dataKey, format, optional }) => {
      const series = getSeries(analytics, months)
      const selectedPoint = series?.find(point => point.month === months[1])

      if (!series || (optional && selectedPoint?.[dataKey] == null)) {
        return { id, label, value: PLACEHOLDER_VALUE, deltaPercent: null }
      }

      const { current, deltaPercent } = getMonthOverMonth(
        series,
        dataKey,
        selected,
      )

      return { id, label, value: format(current), deltaPercent }
    },
  )
}
